// App.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import { useAutoLogout } from "./hooks/useAutoLogout";
import AutoLogoutWarningModal from "./components/AutoLogoutWarningModal";

import Navbar from "./components/homepage/navbar";
import Landing from "./components/login/landing";
import SignIn from "./components/login/signin";
import SignUp from "./components/login/signup";
import HomePage from "./components/homepage/homepage";
import TopicsPage from "./components/pages/topicspage";
import Profile from "./components/pages/profile";
import Foundation from "./components/pages/foundation";
import Higher from "./components/pages/higher";
import Formula from "./components/pages/formula";
import PrivateRoute from "./components/privateroute";
import Algebra from "./components/pages/topics/algebra";
import Geometry from "./components/pages/topics/geometry";
import Numbers from "./components/pages/topics/number";
import Probability from "./components/pages/topics/probability";
import Ratio from "./components/pages/topics/ratio";
import Statistics from "./components/pages/topics/statistics";
import Quiz from "./components/pages/quiz";
import QuizTopic from "./components/pages/quizTopic";
import QuizAttempts from "./components/pages/quizAttempts";
import QuizHistory from "./components/pages/quizHistory";
import ExamBoard from "./components/pages/exams/examboard";
import Edexcel from "./components/pages/exams/edexcel";
import AdminTopics from "./components/pages/admin/adminTopics/adminTopics";
import AdminExam from "./components/pages/admin/adminExam/adminExam";
import AdminEdexcel from "./components/pages/admin/adminExam/adminEdexcel";
import AdminAQA from "./components/pages/admin/adminExam/adminAQA";
import AdminOCR from "./components/pages/admin/adminExam/adminOcr";
import AQA from "./components/pages/exams/aqa";
import OCR from "./components/pages/exams/ocr";
import CompletedTopics from "./components/pages/completedTopics";
import ExamTracking from "./components/pages/exams/examTracking";
import Progress from "./components/homepage/progress";

import { supabase } from "./supaBaseClient";

import { API_BASE } from "./config";

function App() {
  // Auth + role state
  const [userUID, setUserUID] = useState(() => localStorage.getItem("userUID") || null);
  const [userRole, setUserRole] = useState(null);
  const [authReady, setAuthReady] = useState(false); // ensure we don't redirect before Supabase restores session

  const location = useLocation();
  const navigate = useNavigate();
  
  // Prevent duplicate toast notifications
  const hasShownSignupToast = useRef(false);

  // Routes where navbar should be hidden
  const hideNavbarPaths = ["/", "/signin", "/signup"];

  // ============================================
  // AUTO-LOGOUT CONFIGURATION
  // ============================================

  // Auto-logout state
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [logoutWarningTime, setLogoutWarningTime] = useState(0);
  const isLoggingOutRef = useRef(false); // Prevent duplicate logout calls

  // Shared logout function
  const handleAutoLogout = useCallback(async () => {
    // Prevent duplicate calls
    if (isLoggingOutRef.current) {
      console.log('⚠️ Logout already in progress, skipping duplicate call');
      return;
    }

    isLoggingOutRef.current = true;

    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) throw error;
    } catch (e) {
      console.warn("Auto-logout signOut had an issue:", e);
    } finally {
      // Dismiss any existing toasts before showing new one
      toast.dismiss();
      
      localStorage.removeItem("userUID");
      setUserUID(null);
      setShowLogoutWarning(false);
      navigate("/signin", { replace: true });
      
      // Show toast only once
      toast.info("You have been logged out due to inactivity.", { autoClose: 3000 });
      
      // Reset flag after a delay to allow navigation
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 1000);
    }
  }, [navigate, setUserUID]);

  // Handle warning from auto-logout hook
  const handleLogoutWarning = useCallback((timeRemaining) => {
    console.log('⚠️ Warning triggered! Time remaining:', Math.ceil(timeRemaining / 1000), 'seconds');
    setLogoutWarningTime(timeRemaining); // Set initial time once
    setShowLogoutWarning(true);
  }, []);

  // Auto-logout hook - only active when user is logged in
  const shouldEnableAutoLogout = !!userUID && !hideNavbarPaths.includes(location.pathname);

  const { resetActivity, triggerLogout } = useAutoLogout(
    shouldEnableAutoLogout ? handleAutoLogout : null,
    shouldEnableAutoLogout ? handleLogoutWarning : null,
    {
      timeoutMinutes: 60,
      warningMinutes: 2,
      checkInterval: 1000,
    }
  );

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    console.log('✅ User clicked Stay Logged In');
    setShowLogoutWarning(false);
    resetActivity(); // Reset timer
  }, [resetActivity]);

  // Handle "Logout Now" button (also called when modal countdown reaches 0)
  const handleLogoutNow = useCallback(() => {
    console.log('🚪 Logout triggered (user clicked or countdown reached 0)');
    setShowLogoutWarning(false);
    // Mark logout as triggered in the hook to prevent it from interfering
    if (triggerLogout) {
      triggerLogout();
    } else {
      // Fallback if triggerLogout is not available
      handleAutoLogout();
    }
  }, [triggerLogout, handleAutoLogout]);

  // ============================================
  // END AUTO-LOGOUT CONFIGURATION
  // ============================================

  // Persist UID to localStorage
  useEffect(() => {
    if (userUID) {
      localStorage.setItem("userUID", userUID);
    } else {
      localStorage.removeItem("userUID");
    }
  }, [userUID]);

  // --- Auth bootstrap: recover session after OAuth redirect & watch for changes ---
 // --- Auth bootstrap: recover session after OAuth redirect & watch for changes ---
useEffect(() => {
  let unsubscribe = () => {};

  (async () => {
    // 1) Restore existing session on first load
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      setUserUID(session.user.id);
      // Don't redirect immediately - let the role check handle the redirect
    }
    setAuthReady(true);

    // 2) React only to meaningful auth events
    const { data } = supabase.auth.onAuthStateChange((event, nextSession) => {
      switch (event) {
        case "SIGNED_IN": {
          if (nextSession?.user?.id) {
            setUserUID(nextSession.user.id);
            // Don't redirect immediately - let the role check handle the redirect
          }
          break;
        }
        case "SIGNED_OUT": {
          setUserUID(null);
          navigate("/signin", { replace: true });
          break;
        }
        // Don't navigate on TOKEN_REFRESH, USER_UPDATED, etc.
        default:
          break;
      }
    });

    unsubscribe = () => data.subscription.unsubscribe();
  })();

  return () => unsubscribe();
}, [navigate, location.pathname]);

  // Fetch user role from backend and handle redirects
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!userUID) {
        setUserRole(null);
        hasShownSignupToast.current = false; // Reset toast flag when no user
        return;
      }
      try {
        const response = await fetch(`${API_BASE}/api/admin/role/${userUID}`);
        if (response.ok) {
          const data = await response.json();
          setUserRole(data.role); // "admin" or "user"
          
          // Redirect based on role and current location
          if (data.role && ["/", "/signin", "/signup"].includes(location.pathname)) {
            if (data.role === "admin") {
              navigate("/admintopics", { replace: true });
            } else if (data.role === "user") {
              navigate("/home", { replace: true });
            }
          }
        } else if (response.status === 404) {
          // User exists in Supabase but not in our database - needs to complete signup
          setUserRole(null);
          if (["/", "/signin", "/home"].includes(location.pathname) && !hasShownSignupToast.current) {
            hasShownSignupToast.current = true;
            navigate("/signup", { replace: true });
          }
        } else {
          console.error("Failed to fetch user role.");
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    fetchUserRole();
  }, [userUID, API_BASE, location.pathname, navigate]);

  // Guard routes once auth state is ready (prevents flashing back to /signin after OAuth)
  useEffect(() => {
    if (!authReady) return;
    const publicPaths = ["/", "/signin", "/signup"];
    if (!userUID && !publicPaths.includes(location.pathname)) {
      navigate("/signin", { replace: true });
    }
  }, [authReady, userUID, location, navigate]);

  // Clear warning when user logs out
  useEffect(() => {
    if (!userUID) {
      setShowLogoutWarning(false);
    }
  }, [userUID]);

  return (
    <>
      <ToastContainer limit={1} newestOnTop closeOnClick pauseOnHover draggable={false} />

      {/* Auto-logout warning modal */}
      {userUID && (
        <AutoLogoutWarningModal
          isOpen={showLogoutWarning}
          initialTimeRemaining={logoutWarningTime}
          onStayLoggedIn={handleStayLoggedIn}
          onLogoutNow={handleLogoutNow}
        />
      )}

      {!hideNavbarPaths.includes(location.pathname) && (
        <Navbar userUID={userUID} setUserUID={setUserUID} userRole={userRole} />
      )}

      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/signin" element={<SignIn setUserUID={setUserUID} />} />
        <Route path="/signup" element={<SignUp setUserUID={setUserUID} />} />

        {/* Authenticated routes for users */}
        {userRole === "user" && (
          <>
            <Route
              path="/home"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <HomePage userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/topicspage"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <TopicsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Profile userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/foundation"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Foundation />
                </PrivateRoute>
              }
            />
            <Route
              path="/higher"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Higher />
                </PrivateRoute>
              }
            />
            <Route
              path="/formula"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Formula />
                </PrivateRoute>
              }
            />
            <Route
              path="/quiz"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Quiz />
                </PrivateRoute>
              }
            />
            <Route
              path="/quizzes/:topicId"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <QuizTopic userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/algebra"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Algebra />
                </PrivateRoute>
              }
            />
            <Route
              path="/geometry"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Geometry />
                </PrivateRoute>
              }
            />
            <Route
              path="/number"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Numbers />
                </PrivateRoute>
              }
            />
            <Route
              path="/probability"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Probability />
                </PrivateRoute>
              }
            />
            <Route
              path="/ratio"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Ratio />
                </PrivateRoute>
              }
            />
            <Route
              path="/statistics"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Statistics />
                </PrivateRoute>
              }
            />
            <Route
              path="/examboard"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <ExamBoard />
                </PrivateRoute>
              }
            />
            <Route
              path="/edexcel"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Edexcel />
                </PrivateRoute>
              }
            />
            <Route
              path="/aqa"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <AQA />
                </PrivateRoute>
              }
            />
            <Route
              path="/ocr"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <OCR />
                </PrivateRoute>
              }
            />
            <Route
              path="/quizattempts/:topicId"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <QuizAttempts userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/quizhistory"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <QuizHistory userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/completedTopics"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <CompletedTopics userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/examtracking"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <ExamTracking userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Progress userUID={userUID} />
                </PrivateRoute>
              }
            />
          </>
        )}


        {/* Authenticated routes for admins */}
        {userRole === "admin" && (
          <>
            <Route
              path="/profile"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <Profile userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/admintopics"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <AdminTopics userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/adminexam"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <AdminExam userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/adminedexcel"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <AdminEdexcel userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/adminaqa"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <AdminAQA userUID={userUID} />
                </PrivateRoute>
              }
            />
            <Route
              path="/adminocr"
              element={
                <PrivateRoute isAuthenticated={!!userUID} userRole={userRole}>
                  <AdminOCR userUID={userUID} />
                </PrivateRoute>
              }
            />
          </>
        )}
      </Routes>
    </>
  );
}

export default App;